export const SCENE_ID = 'minihra-prirazovani-stop'
export const BUBBLE_DISPLAY_MS = 4000
export const WRONG_BUBBLE_COUNT = 5
export const LEAF_COUNT = 20
export const LEAF_VARIANT_COUNT = 5
export const CLEAN_SOUND_COUNT = 4

/**
 * Maps AnimalId to the filename stem used in stopy/zvirata/cary assets.
 * Most match directly; divocak uses "prase" in file names.
 */
export const ANIMAL_FILE_STEM: Record<AnimalId, string> = {
  liska: 'liska',
  zajic: 'zajic',
  bazant: 'bazant',
  divocak: 'prase',
  veverka: 'veverka',
  jelen: 'jelen',
}

export type AnimalId = 'liska' | 'zajic' | 'bazant' | 'divocak' | 'veverka' | 'jelen'

export interface ItemPosition {
  left: number
  top: number
  width: number
}

/**
 * Stopy (tracks) order on the LEFT side, top to bottom.
 * This order combined with zvirata order on the right creates crossing lines.
 */
export const STOPY_ORDER: AnimalId[] = ['jelen', 'bazant', 'divocak', 'liska', 'veverka', 'zajic']

/**
 * Zvirata (animals) order on the RIGHT side, top to bottom.
 */
export const ZVIRATA_ORDER: AnimalId[] = ['liska', 'zajic', 'bazant', 'divocak', 'veverka', 'jelen']

export const STOPY_POSITIONS: Record<AnimalId, ItemPosition> = {
  jelen: { left: 6, top: 2.5, width: 20 },
  bazant: { left: 7, top: 20, width: 18 },
  divocak: { left: 4, top: 43, width: 21 },
  liska: { left: 4, top: 61.5, width: 22 },
  veverka: { left: 3, top: 98.5, width: 23 },
  zajic: { left: 5, top: 77, width: 20 },
}

export const ZVIRATA_POSITIONS: Record<AnimalId, ItemPosition> = {
  liska: { left: 64, top: 2, width: 26 },
  zajic: { left: 64, top: 18, width: 23 },
  bazant: { left: 61, top: 40, width: 29 },
  divocak: { left: 58, top: 60, width: 32 },
  veverka: { left: 64, top: 77, width: 24 },
  jelen: { left: 61, top: 93, width: 29 },
}

export interface CaraPosition {
  left: number
  top: number
  width: number
  height: number
}

export const CARY_POSITIONS: Record<AnimalId, CaraPosition> = {
  liska: { left: 17, top: 6, width: 48, height: 61 },
  zajic: { left: 22, top: 27, width: 48, height: 54 },
  bazant: { left: 22, top: 25, width: 54, height: 24 },
  divocak: { left: 22, top: 48, width: 48, height: 14 },
  veverka: { left: 15, top: 84, width: 60, height: 23 },
  jelen: { left: 25, top: 8, width: 48, height: 94 },
}

/** Bubble names shown on correct match */
export const CORRECT_BUBBLE_NAMES: Record<AnimalId, string> = {
  liska: 'bubble-liska',
  zajic: 'bubble-zajic',
  bazant: 'bubble-bazant',
  divocak: 'bubble-divocak',
  veverka: 'bubble-veverka',
  jelen: 'bubble-jelen',
}

export interface LeafDef {
  left: number
  top: number
  width: number
  rotation: number
  variant: number
}

function createSeededRandom(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 0x100000000
  }
}

export function buildLeaves(): LeafDef[] {
  const random = createSeededRandom(20260319)
  const leaves: LeafDef[] = []

  const zones = [
    { minX: 2, maxX: 35, minY: 0, maxY: 20 },
    { minX: 55, maxX: 92, minY: 0, maxY: 20 },
    { minX: 2, maxX: 35, minY: 15, maxY: 35 },
    { minX: 55, maxX: 92, minY: 15, maxY: 35 },
    { minX: 25, maxX: 70, minY: 10, maxY: 30 },
    { minX: 2, maxX: 35, minY: 30, maxY: 50 },
    { minX: 55, maxX: 92, minY: 30, maxY: 50 },
    { minX: 25, maxX: 70, minY: 35, maxY: 55 },
    { minX: 2, maxX: 35, minY: 45, maxY: 65 },
    { minX: 55, maxX: 92, minY: 45, maxY: 65 },
    { minX: 25, maxX: 70, minY: 55, maxY: 75 },
    { minX: 2, maxX: 35, minY: 60, maxY: 80 },
    { minX: 55, maxX: 92, minY: 60, maxY: 80 },
    { minX: 25, maxX: 70, minY: 70, maxY: 88 },
    { minX: 2, maxX: 35, minY: 75, maxY: 95 },
    { minX: 55, maxX: 92, minY: 75, maxY: 95 },
    { minX: 10, maxX: 50, minY: 85, maxY: 98 },
    { minX: 40, maxX: 85, minY: 85, maxY: 98 },
    { minX: 30, maxX: 65, minY: 42, maxY: 62 },
    { minX: 15, maxX: 80, minY: 20, maxY: 45 },
  ]

  for (let i = 0; i < LEAF_COUNT; i++) {
    const zone = zones[i % zones.length]
    const left = zone.minX + random() * (zone.maxX - zone.minX - 12)
    const top = zone.minY + random() * (zone.maxY - zone.minY - 8)
    leaves.push({
      left,
      top,
      width: 20 + random() * 16,
      rotation: random() * 360,
      variant: Math.floor(random() * LEAF_VARIANT_COUNT) + 1,
    })
  }

  return leaves
}
